import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

interface User {
	id: string;
	email?: string;
	username?: string;
	full_name?: string;
	first_name?: string;
	last_name?: string;
	manual_avatar_url?: string;
	google_avatar_url?: string;
}

interface UserState {
	// Current user state
	currentUser: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	lastChecked: number | null;

	// Actions
	setCurrentUser: (user: User | null) => void;
	setLoading: (loading: boolean) => void;
	clearUser: () => void;
	updateUser: (updates: Partial<User>) => void;
}

export const useUserStore = create<UserState>()(
	subscribeWithSelector((set, get) => ({
		// Initial state
		currentUser: null,
		isAuthenticated: false,
		isLoading: true,
		lastChecked: null,

		// Actions
		setCurrentUser: (user) => {
			set({
				currentUser: user,
				isAuthenticated: !!user,
				isLoading: false,
				lastChecked: Date.now(),
			});
		},

		setLoading: (loading) => {
			set({ isLoading: loading });
		},

		clearUser: () => {
			set({
				currentUser: null,
				isAuthenticated: false,
				isLoading: false,
				lastChecked: Date.now(),
			});
		},

		updateUser: (updates) => {
			const current = get().currentUser;
			if (current) {
				set({ currentUser: { ...current, ...updates } });
			}
		},
	})),
);

// Optimized selectors to prevent unnecessary re-renders
export const useCurrentUser = () => useUserStore((state) => state.currentUser);
export const useSetCurrentUser = () =>
	useUserStore((state) => state.setCurrentUser);
export const useIsAuthenticated = () =>
	useUserStore((state) => state.isAuthenticated);
export const useIsUserLoading = () => useUserStore((state) => state.isLoading);
export const useSetUserLoading = () =>
	useUserStore((state) => state.setLoading);
export const useClearUser = () => useUserStore((state) => state.clearUser);
export const useUpdateUser = () => useUserStore((state) => state.updateUser);
